class NavHandler {
    constructor() {
        this.navContainer = document.querySelector(".nav-container");

        this.mobile = {
            drawer: document.getElementById("mobile-nav-drawer"),
            toggleBtn: document.getElementById("menu-toggle"),
            mainMenu: document.getElementById("mobile-nav-main-menu"),
            footer: document.getElementById("mobile-nav-footer"),
            subcategories: document.querySelectorAll(".mobile-subcategory"),
        };

        if (this.navContainer) this.init();
    }

    init() {
        this.setupDesktopDropdowns();
        this.setupMobileNav();
        this.setupBackToTop();
    }

    setupDesktopDropdowns() {
        const isLarge = () => window.matchMedia("(min-width: 1024px)").matches;
        const hoverTimers = new Map();
        const hideTimers = new Map();

        const SHOW_DELAY = 250; // ms to wait before showing dropdown
        const HIDE_DELAY = 150; // ms to wait before hiding dropdown

        const show = (navItem, drop) => {
            if (!isLarge() || !drop) return;

            const timerId = navItem.dataset.id;

            // Clear any pending hide timer
            if (hideTimers.has(timerId)) {
                clearTimeout(hideTimers.get(timerId));
                hideTimers.delete(timerId);
            }

            // Clear any existing show timer
            if (hoverTimers.has(timerId)) {
                clearTimeout(hoverTimers.get(timerId));
            }

            // Set new show timer with delay
            const timer = setTimeout(() => {
                drop.classList.add("drop-nav-show");
                navItem.classList.add("is-open");
                navItem.classList.add("hovering");
                hoverTimers.delete(timerId);
            }, SHOW_DELAY);

            hoverTimers.set(timerId, timer);
        };

        const hide = (navItem, drop) => {
            if (!navItem || !drop) return;

            const timerId = navItem.dataset.id;

            // Clear any pending show timer
            if (hoverTimers.has(timerId)) {
                clearTimeout(hoverTimers.get(timerId));
                hoverTimers.delete(timerId);
            }

            // Clear any existing hide timer
            if (hideTimers.has(timerId)) {
                clearTimeout(hideTimers.get(timerId));
            }

            // Set new hide timer
            const timer = setTimeout(() => {
                drop.classList.remove("drop-nav-show");
                navItem.classList.remove("is-open");
                navItem.classList.remove("hovering");
                hideTimers.delete(timerId);
            }, HIDE_DELAY);

            hideTimers.set(timerId, timer);
        };

        document.addEventListener(
            "mouseenter",
            (e) => {
                if (!e.target || typeof e.target.closest !== "function") return;

                const navItem = e.target.closest(".nav-item[data-id]");
                if (navItem && isLarge()) {
                    const drop = document.querySelector(
                        `.drop-nav[data-id="${navItem.dataset.id}"]`
                    );
                    show(navItem, drop);
                }
                const dropNav = e.target.closest(".drop-nav[data-id]");
                if (dropNav && isLarge()) {
                    const navItem2 = document.querySelector(
                        `.nav-item[data-id="${dropNav.dataset.id}"]`
                    );
                    show(navItem2, dropNav);
                }
            },
            true
        );

        document.addEventListener(
            "mouseleave",
            (e) => {
                if (!e.target || typeof e.target.closest !== "function") return;

                const navItem = e.target.closest(".nav-item[data-id]");
                if (navItem && isLarge()) {
                    const drop = document.querySelector(
                        `.drop-nav[data-id="${navItem.dataset.id}"]`
                    );
                    if (!drop?.matches(":hover") && !navItem.matches(":hover"))
                        hide(navItem, drop);
                }
                const dropNav = e.target.closest(".drop-nav[data-id]");
                if (dropNav && isLarge()) {
                    const navItem2 = document.querySelector(
                        `.nav-item[data-id="${dropNav.dataset.id}"]`
                    );
                    if (
                        !dropNav.matches(":hover") &&
                        !navItem2?.matches(":hover")
                    )
                        hide(navItem2, dropNav);
                }
            },
            true
        );
    }

    setupMobileNav() {
        const { drawer, toggleBtn } = this.mobile;
        if (!toggleBtn || !drawer) return;

        const closeBtn = document.getElementById("mobile-nav-close");

        const openDrawer = () => {
            drawer.classList.remove(
                "rtl:translate-x-full",
                "ltr:-translate-x-full"
            );
            drawer.classList.add("translate-x-0");
            document.body.style.overflow = "hidden";
            toggleBtn.setAttribute("aria-pressed", "true");
        };

        const closeDrawer = () => {
            drawer.classList.remove("translate-x-0");
            const isRtl =
                document.documentElement.dir === "rtl" ||
                document.documentElement.lang === "ar";
            if (isRtl) {
                drawer.classList.add("rtl:translate-x-full");
                drawer.classList.remove("ltr:-translate-x-full");
            } else {
                drawer.classList.add("ltr:-translate-x-full");
                drawer.classList.remove("rtl:translate-x-full");
            }
            document.body.style.overflow = "";
            toggleBtn.setAttribute("aria-pressed", "false");
            // Reset split panel state
            this.resetMobilePanels();
        };

        // Reset panels to initial state
        this.resetMobilePanels = () => {
            const parentBtns = drawer.querySelectorAll(".mobile-parent-btn");
            const childPanels = drawer.querySelectorAll(".mobile-children-panel");
            const grandchildPanels = drawer.querySelectorAll(".mobile-grandchildren");
            const expandBtns = drawer.querySelectorAll(".mobile-expand-btn");

            // Reset parent buttons - activate first one
            parentBtns.forEach((btn, index) => {
                btn.classList.toggle("active", index === 0);
            });

            // Reset child panels - show first one
            childPanels.forEach((panel, index) => {
                panel.classList.toggle("hidden", index !== 0);
            });

            // Collapse all grandchildren
            grandchildPanels.forEach((panel) => panel.classList.add("hidden"));
            expandBtns.forEach((btn) => btn.classList.remove("expanded"));
        };

        // Handle parent button clicks - switch children panel
        const handleParentClick = (parentBtn) => {
            const parentId = parentBtn.getAttribute("data-parent-id");
            if (!parentId) return;

            // Update parent button states
            const allParentBtns = drawer.querySelectorAll(".mobile-parent-btn");
            allParentBtns.forEach((btn) => {
                btn.classList.toggle("active", btn === parentBtn);
            });

            // Show corresponding children panel
            const allChildPanels = drawer.querySelectorAll(".mobile-children-panel");
            allChildPanels.forEach((panel) => {
                panel.classList.toggle("hidden", panel.id !== `children-${parentId}`);
            });
        };

        // Handle expand button clicks - toggle grandchildren
        const handleExpandClick = (expandBtn) => {
            const expandId = expandBtn.getAttribute("data-expand-id");
            if (!expandId) return;

            const grandchildPanel = document.getElementById(`grandchildren-${expandId}`);

            if (grandchildPanel) {
                grandchildPanel.classList.toggle("hidden");
            }
            expandBtn.classList.toggle("expanded");
        };

        // Toggle button
        toggleBtn.addEventListener("click", (e) => {
            e.preventDefault();
            if (drawer.classList.contains("translate-x-0")) {
                closeDrawer();
            } else {
                openDrawer();
            }
        });

        // Close button inside drawer
        if (closeBtn) {
            closeBtn.addEventListener("click", (e) => {
                e.preventDefault();
                closeDrawer();
            });
        }

        // Delegated clicks
        document.addEventListener("click", (e) => {
            const target = e.target;
            if (!target || typeof target.closest !== "function") return;

            if (target.closest("#menu-toggle") || target.closest("#mobile-nav-close")) return;

            // Handle parent button clicks (split panel navigation)
            const parentBtn = target.closest(".mobile-parent-btn");
            if (parentBtn) {
                handleParentClick(parentBtn);
                return;
            }

            // Handle expand button clicks (grandchildren toggle)
            const expandBtn = target.closest(".mobile-expand-btn");
            if (expandBtn) {
                handleExpandClick(expandBtn);
                return;
            }

            // Handle panel expand/collapse button
            const panelExpandBtn = target.closest("#mobile-nav-expand-btn");
            if (panelExpandBtn) {
                const parentsPanel = document.getElementById("mobile-nav-parents");
                if (parentsPanel) {
                    parentsPanel.classList.toggle("expanded");
                    panelExpandBtn.classList.toggle("expanded");
                }
                return;
            }

            // Close drawer when clicking outside
            if (
                !drawer.contains(target) &&
                !toggleBtn.contains(target) &&
                drawer.classList.contains("translate-x-0")
            ) {
                closeDrawer();
            }
        });
    }

    setupBackToTop() {
        const btn = document.getElementById("back-to-top");
        if (!btn) return;
        let isVisible = false;
        let ticking = false;
        const update = () => {
            const shouldShow = window.pageYOffset > 300;
            if (shouldShow !== isVisible) {
                isVisible = shouldShow;
                btn.style.display = isVisible ? "flex" : "none";
            }
            ticking = false;
        };
        window.addEventListener(
            "scroll",
            () => {
                if (!ticking) {
                    requestAnimationFrame(update);
                    ticking = true;
                }
            },
            { passive: true }
        );
        btn.addEventListener("click", () =>
            window.scrollTo({ top: 0, behavior: "smooth" })
        );
    }
}

function initNav() {
    if (window.requestIdleCallback) {
        requestIdleCallback(
            () => {
                new NavHandler();
            },
            { timeout: 200 }
        );
    } else {
        setTimeout(() => {
            new NavHandler();
        }, 100);
    }
}

if (typeof module !== "undefined" && module.exports) {
    module.exports = { NavHandler, initNav };
} else {
    window.NavHandler = NavHandler;
    window.initNav = initNav;
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initNav);
} else {
    initNav();
}
